import { useEffect, useState } from 'react'
import { CheckCheck, Lock, Mail, Plus, Search } from 'lucide-react'
import type { UnitDetails, UnitSearchResult } from '../api/types'
import type { UseQueryResult } from '@tanstack/react-query'
import { useAuth } from '../auth/OfficeAuthProvider'
import { BOL2_BASE_URL } from '../config'
import { useMailboxItem } from '../hooks/useMailboxItem'
import { useSenderResolution } from '../hooks/useSenderResolution'
import { useUnitDetails } from '../hooks/useUnitDetails'
import { apiErrorMessage } from '../lib/apiClient'
import { CreateTicketForm } from './CreateTicketForm'
import { Header } from './Header'
import { ManualSearch } from './ManualSearch'
import { SenderBanner } from './SenderBanner'
import { UnitPicker } from './UnitPicker'
import {
    CreditControlSection,
    FinancialSection,
    PeopleSection,
    PortalAccessSection,
    SenderAndUnitSection,
    TicketsSection,
} from './UnitSections'
import { Button, Card, CentredState, ErrorNotice, Section, Skeleton } from './ui'

type View = { kind: 'context' } | { kind: 'create-ticket' } | { kind: 'created'; ticketId: number }

function LoadingSections({ label }: { label: string }) {
    return (
        <>
            <div className="flex items-center gap-1.5 px-0.5 pb-2 text-[11px] text-foreground/55">
                <span className="size-3 animate-spin rounded-full border-2 border-border border-t-primary" />
                {label}
            </div>
            <Section title="Sender &amp; unit">
                <div className="space-y-2">
                    <Skeleton width="70%" tall />
                    <Skeleton width="92%" />
                    <Skeleton width="55%" />
                </div>
            </Section>
            <Section title="Financial position">
                <div className="space-y-2">
                    <Skeleton width="45%" tall />
                    <Skeleton width="80%" />
                </div>
            </Section>
            <Section title="Credit control">
                <Skeleton width="65%" />
            </Section>
        </>
    )
}

/** The resolved unit's details, plus the entry point into ticket creation. */
function UnitContext({
    unit,
    senderEmail,
    alternatives,
    highlightTicketId,
    details,
    onCreateTicket,
    onChangeUnit,
}: {
    unit: UnitSearchResult
    senderEmail: string | undefined
    alternatives: UnitSearchResult[]
    highlightTicketId?: number
    details: UseQueryResult<UnitDetails>
    onCreateTicket: () => void
    onChangeUnit: () => void
}) {
    if (details.isPending) return <LoadingSections label="Loading unit details…" />

    if (details.isError || !details.data) {
        return (
            <ErrorNotice
                message={apiErrorMessage(details.error, 'Could not load unit details')}
                onRetry={() => void details.refetch()}
            />
        )
    }

    return (
        <>
            <SenderAndUnitSection details={details.data} />
            <FinancialSection details={details.data} />
            <CreditControlSection details={details.data} />
            <TicketsSection details={details.data} highlightTicketId={highlightTicketId} />
            <PeopleSection details={details.data} senderEmail={senderEmail} />
            <PortalAccessSection details={details.data} />

            <div className="mb-2">
                <Button onClick={onCreateTicket}>
                    <Plus size={13} strokeWidth={2.6} />
                    Create ticket from this email
                </Button>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 px-0.5 pb-1 text-[10px] text-foreground/50">
                <a
                    href={`${BOL2_BASE_URL}/rel3/admin/BlockArea?block_id=${unit.block_id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="underline hover:text-foreground"
                >
                    Open block in Blocks Online
                </a>
                {alternatives.length > 0 && (
                    <button type="button" onClick={onChangeUnit} className="underline hover:text-foreground">
                        Change unit ({alternatives.length + 1})
                    </button>
                )}
            </div>
        </>
    )
}

export function App() {
    const { status, profile, error: authError, signIn, signOut } = useAuth()
    const item = useMailboxItem()
    const sender = item?.sender ?? null
    const { resolution, select, clearSelection, searchManually, refetch } = useSenderResolution(sender)

    const [view, setView] = useState<View>({ kind: 'context' })
    const [forceManual, setForceManual] = useState(false)

    const matchedUnit = resolution.status === 'matched' ? resolution.unit : null

    // Called unconditionally (the query itself is disabled until a unit is resolved) so
    // both the context view and the ticket form read from one cache entry.
    const details = useUnitDetails(matchedUnit?.unit_id, matchedUnit?.block_id)

    // Switching email should always return the pane to the context view.
    useEffect(() => {
        setView({ kind: 'context' })
        setForceManual(false)
    }, [item?.key])

    if (status === 'loading') {
        return (
            <div className="flex h-full items-center justify-center">
                <span className="size-6 animate-spin rounded-full border-2 border-border border-t-primary" />
            </div>
        )
    }

    // Signed out: no header at all, just the sign-in prompt.
    if (status === 'signed-out' || !profile) {
        return (
            <div className="flex h-full flex-col">
                <CentredState
                    fill
                    icon={<Lock size={22} strokeWidth={2} />}
                    title="Sign in to Blocks Online"
                    body="Connect to your BOL2 account to see sender details for this email."
                >
                    <div className="w-full max-w-[210px]">
                        <Button onClick={() => void signIn()}>Sign in</Button>
                    </div>
                    {authError ? (
                        <p className="m-0 text-[10.5px] text-[hsl(4_72%_38%)]">{authError}</p>
                    ) : (
                        <p className="m-0 max-w-[30ch] text-[10.5px] text-foreground/50">
                            Opens a secure BOL2 login window. Two-factor authentication is supported.
                        </p>
                    )}
                </CentredState>
            </div>
        )
    }

    return (
        <div className="flex h-full flex-col">
            <Header profile={profile} onSignOut={signOut} />

            <main className="flex flex-1 flex-col overflow-y-auto p-2.5">
                {!item || !sender ? (
                    <CentredState
                        fill
                        icon={<Mail size={21} strokeWidth={2} />}
                        title="No email selected"
                        body="Open a message in the reading pane to see who it's from."
                    />
                ) : view.kind === 'create-ticket' && matchedUnit ? (
                    <CreateTicketForm
                        item={item}
                        unit={matchedUnit}
                        details={details.data}
                        onCancel={() => setView({ kind: 'context' })}
                        onCreated={(ticketId) => setView({ kind: 'created', ticketId })}
                    />
                ) : (
                    <>
                        {view.kind === 'created' && (
                            <Card>
                                <CentredState
                                    icon={<CheckCheck size={22} strokeWidth={2.6} />}
                                    title={`Ticket #${view.ticketId} created`}
                                    body="Open, assigned to the property manager, visible to the leaseholder."
                                >
                                    <a
                                        href={`${BOL2_BASE_URL}/rel3/admin/TicketDetailsController?ticket_id=${view.ticketId}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-[11px] underline hover:text-foreground"
                                    >
                                        Open in Blocks Online
                                    </a>
                                </CentredState>
                            </Card>
                        )}

                        <SenderBanner
                            sender={sender}
                            subject={item.subject}
                            matched={resolution.status === 'matched'}
                        />

                        {resolution.status === 'loading' && (
                            <LoadingSections label="Looking up sender in Blocks Online…" />
                        )}

                        {resolution.status === 'error' && (
                            <ErrorNotice message={resolution.message} onRetry={() => void refetch()} />
                        )}

                        {resolution.status === 'choose' && !forceManual && (
                            <UnitPicker
                                matches={resolution.matches}
                                intro={
                                    <>
                                        {resolution.via === 'name'
                                            ? 'No email match, but this name matches '
                                            : 'This sender is linked to '}
                                        <strong className="text-foreground">
                                            {resolution.matches.length} units
                                        </strong>
                                        . Choose one to continue.
                                    </>
                                }
                                onSelect={select}
                                onSearchInstead={() => setForceManual(true)}
                            />
                        )}

                        {(resolution.status === 'not-found' || forceManual) && (
                            <>
                                {!forceManual && (
                                    <CentredState
                                        icon={<Search size={21} strokeWidth={2} />}
                                        title="No match for this sender"
                                        body={
                                            <>
                                                We couldn't match this address, or the display name “
                                                {sender.displayName}”, to a unit.
                                            </>
                                        }
                                    />
                                )}
                                <ManualSearch onSearch={searchManually} />
                                <p className="mt-1 mb-0 text-center text-[10px] text-foreground/50">
                                    Email matching only finds contacts with a portal user record.
                                </p>
                            </>
                        )}

                        {resolution.status === 'matched' && (
                            <UnitContext
                                unit={resolution.unit}
                                senderEmail={sender.emailAddress}
                                alternatives={resolution.alternatives}
                                highlightTicketId={view.kind === 'created' ? view.ticketId : undefined}
                                details={details}
                                onCreateTicket={() => setView({ kind: 'create-ticket' })}
                                onChangeUnit={clearSelection}
                            />
                        )}
                    </>
                )}
            </main>
        </div>
    )
}
